// src/components/common/AutocompleteClientiAsync.tsx
import React, {useState, useEffect, useCallback} from 'react';
import {
    Autocomplete,
    TextField,
    CircularProgress,
    Box,
    Typography,
    AutocompletePropsSizeOverrides
} from '@mui/material';
import {SxProps, Theme} from '@mui/material/styles';
import {debounce} from 'lodash';
import {handleResponse, handleError, headerset} from 'api/apiUtils';
import {ClienteLight} from 'components/anagrafiche/clienti/clientiSlice';

export interface AutocompleteClientiAsyncProps {
    label?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    value?: ClienteLight | null;
    onChange?: (cliente: ClienteLight | null) => void;
    size?: "small" | "medium" | undefined
    // Numero minimo di caratteri prima di iniziare la ricerca
    minSearchLength?: number;
    // Delay in millisecondi per il debounce
    debounceDelay?: number;
    // Prop sx per personalizzare lo stile
    sx?: SxProps<Theme>;
}


// API per la ricerca dei clienti
async function searchClienti(
    searchText: string,
    limit: number = 20
): Promise<{ results: ClienteLight[]; count: number }> {
    let url = `${process.env.API_URL}/api/anagrafiche/clienti/search/`;

    const params = new URLSearchParams();
    if (searchText) params.append('q', searchText);
    params.append('limit', limit.toString());

    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;

    return fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

// Versione standalone del componente
export const AutocompleteClientiAsync: React.FC<AutocompleteClientiAsyncProps> = ({
                                                                                      label = 'Cliente',
                                                                                      required = false,
                                                                                      disabled = false,
                                                                                      error = false,
                                                                                      helperText = '',
                                                                                      value = null,
                                                                                      onChange,
                                                                                      size = 'medium',
                                                                                      minSearchLength = 2,
                                                                                      debounceDelay = 300,
                                                                                      sx,
                                                                                  }) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<ClienteLight[]>([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [totalCount, setTotalCount] = useState(0);

    // Funzione di ricerca con debounce
    const fetchClienti = useCallback(
        debounce(async (searchText: string) => {
            if (searchText.length < minSearchLength) {
                setOptions([]);
                setTotalCount(0);
                return;
            }

            setLoading(true);
            try {
                const response = await searchClienti(searchText);
                setOptions(response.results);
                setTotalCount(response.count);
            } catch (error) {
                console.error('Errore nella ricerca clienti:', error);
                setOptions([]);
                setTotalCount(0);
            } finally {
                setLoading(false);
            }
        }, debounceDelay),
        [minSearchLength]
    );

    // Effettua la ricerca quando cambia l'input
    useEffect(() => {
        if (inputValue) {
            fetchClienti(inputValue);
        } else {
            setOptions([]);
            setTotalCount(0);
        }
    }, [inputValue, fetchClienti]);

    // Stile di default basato su size
    const defaultSx: SxProps<Theme> = {
        '& .MuiInputBase-input': {
            fontSize: size === 'small' ? '0.8rem' : 'inherit',
            padding: '6px 8px',
        },
        '& .MuiInputLabel-root': {
            fontSize: size === 'small' ? '0.8rem' : 'inherit',
        },
        '& .MuiFormHelperText-root': {
            fontSize: size === 'small' ? '0.8rem' : 'inherit',
        },
    };

    return (
        <Autocomplete
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            value={value}
            size={size}
            onChange={(event, newValue) => {
                onChange?.(newValue);
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
            }}
            options={options}
            loading={loading}
            disabled={disabled}
            getOptionLabel={(option) =>
                `${option.nome_cliente}`
            }

            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={
                inputValue.length < minSearchLength || !!value
                    ? `Digita almeno ${minSearchLength} caratteri per cercare`
                    : 'Nessun componente trovato'
            }
            sx={sx}
            renderOption={(props, option) => (
                <Box component="li" {...props}>
                    <Box>
                        <Typography variant="body1" sx={{fontSize: size == 'small' ? '0.8rem' : 'inherit'}}>
                            {option.nome_cliente}
                        </Typography>
                        {/*<Typography variant="caption" color="text.secondary"*/}
                        {/*            sx={{fontSize: size == 'small' ? '0.7rem' : 'inherit'}}>*/}
                        {/*    ({option.id})*/}
                        {/*</Typography>*/}
                    </Box>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    // size="small"  // Per dimensione compatta generale
                    error={error}
                    helperText={
                        helperText ||
                        (totalCount > options.length &&
                            `Mostrati ${options.length} di ${totalCount} risultati. Affina la ricerca.`)
                    }
                    placeholder="Seleziona cliente"
                    sx={sx || defaultSx}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20}/> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
        />
    );
};

// Versione per React Hook Form

// Hook personalizzato per la gestione dei clienti (opzionale)
export const useAutocompleteClientiAsync = (
    initialValue?: ClienteLight | null,
) => {
    const [selectedCliente, setSelectedCliente] = useState<ClienteLight | null>(initialValue || null);
    const [isLoading, setIsLoading] = useState(false);

    // Funzione per caricare un cliente specifico per ID (utile per edit forms)
    const loadClienteById = useCallback(async (clienteId: number) => {
        setIsLoading(true);
        try {
            const response = await fetch(
                `${process.env.API_URL}/api/anagrafiche/clienti/${clienteId}/`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization: headerset().Authorization,
                    },
                }
            );
            const cliente = await handleResponse(response);
            setSelectedCliente(cliente);
            return cliente;
        } catch (error) {
            console.error('Errore nel caricamento del cliente:', error);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, []);

    return {
        selectedCliente,
        setSelectedCliente,
        loadClienteById,
        isLoading,
    };
};